import { apiAuth, apiResponseType } from ".";

/**
 * Ferramenta emergencial de correção manual de balanços (Super Admin).
 * Os valores de balanço e valor preso são quantidades inteiras de cotas.
 */
export type BalanceAdjustmentType =
  | "seller"
  | "unity"
  | "contest_product"
  | "leftover_product";

export interface BalanceAdjustmentFilters {
  contest_id?: (number | string)[];
  cp_classification_id?: (number | string)[];
  unity_id?: (number | string)[];
  seller_id?: (number | string)[];
  per_page?: number;
}

export interface BalanceAdjustmentRow {
  id: number;
  uuid: string;
  balance: number;
  with_held_balance: number;
  contest_id: number | null;
  cp_classification_id: number | null;
  unity_id?: number | null;
  seller_id?: number | null;
  leftover_product_id?: number | null;
  seller_name?: string | null;
  unity_name?: string | null;
  contest_name?: string | null;
  contest_validation_date?: string | null;
  lottery_name?: string | null;
  product_name?: string | null;
  product_classification_name?: string | null;
  updated_at?: string | null;
}

export interface BalanceAdjustmentProductOption {
  id: number;
  contest_id: number;
  product_name: string;
  product_classification_id: number;
  contest_name?: string | null;
}

export interface BalanceAdjustmentAuditRow {
  id: number;
  uuid: string;
  balance_type: BalanceAdjustmentType;
  balance_table: string;
  balance_record_id: number;
  balance_record_uuid: string | null;
  previous_balance: number;
  new_balance: number;
  previous_with_held_balance: number;
  new_with_held_balance: number;
  reason: string | null;
  ip_address: string | null;
  created_at: string;
  user_name: string | null;
  contest_name: string | null;
  unity_name: string | null;
  seller_name: string | null;
  product_name: string | null;
}

const appendArray = (
  params: URLSearchParams,
  key: string,
  values?: (number | string)[]
): void => {
  if (!values || values.length === 0) return;

  values.forEach((value) => params.append(`${key}[]`, value.toString()));
};

const buildQuery = (page: number, filters?: BalanceAdjustmentFilters): string => {
  const params = new URLSearchParams();
  params.set("page", page.toString());

  appendArray(params, "contest_id", filters?.contest_id);
  appendArray(params, "cp_classification_id", filters?.cp_classification_id);
  appendArray(params, "unity_id", filters?.unity_id);
  appendArray(params, "seller_id", filters?.seller_id);

  if (filters?.per_page) params.set("per_page", filters.per_page.toString());

  return params.toString();
};

export const getSellerBalanceAdjustments = (
  page: number = 1,
  filters?: BalanceAdjustmentFilters
): apiResponseType =>
  apiAuth.get(`balance-adjustments/sellers?${buildQuery(page, filters)}`);

export const getUnityBalanceAdjustments = (
  page: number = 1,
  filters?: BalanceAdjustmentFilters
): apiResponseType =>
  apiAuth.get(`balance-adjustments/unities?${buildQuery(page, filters)}`);

export const getContestProductBalanceAdjustments = (
  page: number = 1,
  filters?: BalanceAdjustmentFilters
): apiResponseType =>
  apiAuth.get(`balance-adjustments/contest-products?${buildQuery(page, filters)}`);

export const getLeftoverProductBalanceAdjustments = (
  page: number = 1,
  filters?: BalanceAdjustmentFilters
): apiResponseType =>
  apiAuth.get(`balance-adjustments/leftover-products?${buildQuery(page, filters)}`);

export const getBalanceAdjustmentProducts = (
  contestIds?: (number | string)[]
): apiResponseType => {
  const params = new URLSearchParams();
  appendArray(params, "contest_id", contestIds);

  const query = params.toString();

  return apiAuth.get(`balance-adjustments/products/select${query ? `?${query}` : ""}`);
};

export const getBalanceAdjustmentAudits = (
  page: number = 1,
  filters?: BalanceAdjustmentFilters & { balance_type?: BalanceAdjustmentType[] }
): apiResponseType => {
  const params = new URLSearchParams();
  params.set("page", page.toString());

  appendArray(params, "balance_type", filters?.balance_type);
  appendArray(params, "contest_id", filters?.contest_id);
  appendArray(params, "unity_id", filters?.unity_id);
  appendArray(params, "seller_id", filters?.seller_id);

  return apiAuth.get(`balance-adjustments/audits?${params.toString()}`);
};

export const updateBalanceAdjustment = (
  type: BalanceAdjustmentType,
  uuid: string,
  data: {
    balance: number;
    with_held_balance: number;
    reason?: string;
    confirmed: boolean;
  }
): apiResponseType => apiAuth.put(`balance-adjustments/${type}/${uuid}/update`, data);

/* ------------------------------------------------------------------ */
/* Consolidação de duplicidades - balanço de vendedor                  */
/* ------------------------------------------------------------------ */

export interface DuplicateRow {
  id: number;
  uuid: string;
  balance: number;
  with_held_balance: number;
  created_at: string | null;
  updated_at: string | null;
  will_keep: boolean;
}

export interface DuplicateGroup {
  cp_classification_id: number;
  product_name: string;
  product_classification_name: string | null;
  keep_row_id: number;
  total_balance: number;
  total_with_held_balance: number;
  rows: DuplicateRow[];
}

export interface DuplicatePreview {
  seller: { id: number; name: string; unity_name: string | null };
  contest: { id: number; name: string };
  groups_found: number;
  rows_involved: number;
  rows_to_remove: number;
  totals_before: { balance: number; with_held_balance: number };
  groups: DuplicateGroup[];
  warnings: string[];
  preview_hash: string;
}

export interface DuplicateConsolidationResult {
  seller: { id: number; name: string };
  contest: { id: number; name: string };
  groups_consolidated: number;
  rows_consolidated: number;
  rows_kept: {
    id: number;
    cp_classification_id: number;
    product_name: string;
    balance: number;
    with_held_balance: number;
  }[];
  rows_removed: {
    id: number;
    cp_classification_id: number;
    product_name: string;
    previous_balance: number;
    previous_with_held_balance: number;
  }[];
  distributions_repointed: number;
  balance_flows_repointed: number;
  totals_before: { balance: number; with_held_balance: number };
  totals_after: { balance: number; with_held_balance: number };
  validated: boolean;
}

export const previewBalanceDuplicates = (
  sellerId: number | string,
  contestId: number | string
): apiResponseType =>
  apiAuth.get(
    `balance-adjustments/duplicates/preview?seller_id=${sellerId}&contest_id=${contestId}`
  );

export const consolidateBalanceDuplicates = (data: {
  seller_id: number | string;
  contest_id: number | string;
  preview_hash: string;
  confirmed: boolean;
}): apiResponseType =>
  apiAuth.post(`balance-adjustments/duplicates/consolidate`, data);

/* ------------------------------------------------------------------ */
/* Correção em lote - balanço de vendedor                              */
/* ------------------------------------------------------------------ */

export type BatchOperation = "none" | "set" | "add" | "subtract";

export interface BatchContestOption {
  id: number;
  uuid: string;
  name: string;
  validation_date: string | null;
  is_active: boolean;
  lottery_name: string | null;
  status_name: string | null;
}

export interface BatchPool {
  cp_classification_id: number;
  product_name: string;
  product_classification_name: string | null;
  row_id: number;
  row_uuid: string;
  balance: number;
  with_held_balance: number;
  rows_count: number;
  has_duplicates: boolean;
  selectable: boolean;
}

export interface BatchPoolsResponse {
  seller: { id: number; name: string; unity_name: string | null };
  contest: {
    id: number;
    name: string;
    is_active: boolean;
    status_name: string | null;
  };
  pools: BatchPool[];
  warnings: string[];
}

export interface BatchOperationConfig {
  operation: BatchOperation;
  amount: number | null;
}

export interface BatchPreviewItem {
  cp_classification_id: number;
  product_name: string;
  product_classification_name: string | null;
  row_id: number;
  row_uuid: string;
  current_balance: number;
  current_with_held_balance: number;
  new_balance: number;
  new_with_held_balance: number;
  changed: boolean;
}

export interface BatchPreview {
  seller: { id: number; name: string; unity_name?: string | null };
  contest: { id: number; name: string };
  operations: {
    balance: BatchOperationConfig;
    with_held_balance: BatchOperationConfig;
  };
  pools_selected: number;
  pools_changed: number;
  totals_before: { balance: number; with_held_balance: number };
  totals_after: { balance: number; with_held_balance: number };
  items: BatchPreviewItem[];
  preview_hash: string;
}

export interface BatchApplyResult extends Omit<BatchPreview, "preview_hash"> {
  validated: boolean;
}

export interface BatchIntent {
  seller_id: number | string;
  contest_id: number | string;
  cp_classification_ids: number[];
  balance_operation: BatchOperation;
  balance_amount: number | null;
  with_held_operation: BatchOperation;
  with_held_amount: number | null;
}

/** Todos os concursos, inclusive encerrados e antigos. */
export const getBalanceAdjustmentContests = (): apiResponseType =>
  apiAuth.get(`balance-adjustments/contests/select`);

export const getBatchPools = (
  sellerId: number | string,
  contestId: number | string
): apiResponseType =>
  apiAuth.get(
    `balance-adjustments/batch/pools?seller_id=${sellerId}&contest_id=${contestId}`
  );

export const previewBatchCorrection = (data: BatchIntent): apiResponseType =>
  apiAuth.post(`balance-adjustments/batch/preview`, data);

export const applyBatchCorrection = (
  data: BatchIntent & { preview_hash: string; confirmed: boolean }
): apiResponseType => apiAuth.post(`balance-adjustments/batch/apply`, data);
